import { UserPermission } from '@/enums/scenario-builder.enum'
import { TGFPAReportTemplatePayload } from '@/interfaces/gfpaReport.interface'
import {
  IRunReportTemplateDTO,
  IScheduleReportSimpleReturn2,
  IScheduleRunReportDTO,
  IScheduleRunReportDTO2,
  TBenchmark,
  TPortfolio,
  TReportTemplate,
  TReportTemplateDetails,
  TUserInfo,
} from '@/interfaces/scenarioBuilder.interface'
import axiosInstance from '@/utils/interceptor'

// Cache and request deduplication for report templates
let reportTemplatesCache: TReportTemplate[] | null = null
let reportTemplatesCacheTime: number = 0
let reportTemplatesPromise: Promise<TReportTemplate[]> | null = null
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

export const getReportTemplates = async (): Promise<TReportTemplate[]> => {
  const now = Date.now()

  // Return cached data if it's still valid - avoid multiple requests
  if (reportTemplatesCache && now - reportTemplatesCacheTime < CACHE_DURATION) {
    return reportTemplatesCache
  }

  if (reportTemplatesPromise) {
    return reportTemplatesPromise
  }

  // Create new request
  reportTemplatesPromise = (async () => {
    try {
      const [scenResult, gfpaResult] = await Promise.allSettled([
        axiosInstance.get('/report/templates?reportType=SCEN&reportId=ALL'),
        axiosInstance.get('/report/templates?reportType=GFPAH&reportId=ALL'),
      ])

      const scenTemplates: TReportTemplate[] =
        scenResult.status === 'fulfilled'
          ? ((Array.isArray(scenResult.value.data) ? scenResult.value.data : []) as TReportTemplate[])
          : []

      const gfpaTemplates: TReportTemplate[] =
        gfpaResult.status === 'fulfilled'
          ? ((Array.isArray(gfpaResult.value.data) ? gfpaResult.value.data : []) as TReportTemplate[])
          : []

      const result = [...scenTemplates, ...gfpaTemplates]

      // Cache the result
      reportTemplatesCache = result
      reportTemplatesCacheTime = Date.now()

      return result
    } finally {
      reportTemplatesPromise = null
    }
  })()

  return reportTemplatesPromise
}

export const clearReportTemplatesCache = () => {
  reportTemplatesCache = null
  reportTemplatesCacheTime = 0
}

export const getLoggedInUserInfo = async (): Promise<TUserInfo> => {
  const response = await axiosInstance.get('/authorization/userInfo')
  return response.data as TUserInfo
}

export const getPortfolioList = async (): Promise<TPortfolio[]> => {
  const response = await axiosInstance.get('/portfolio/getPortfolioNames')
  return response.data as TPortfolio[]
}
export const getBenchmarkList = async (): Promise<TBenchmark[]> => {
  const response = await axiosInstance.get('/benchmarkList')
  return response.data as TBenchmark[]
}

export const userHasPermission = (userInfo: TUserInfo, permission: UserPermission): boolean => {
  if (!userInfo) {
    return false
  }
  return userInfo.permissions.includes(permission)
}

export const createReportTemplate = async (payload: TReportTemplateDetails) => {
  const reportType = payload.reportType || 'SCEN'
  const response = await axiosInstance.post(`/report/templates?reportType=${reportType}`, payload)
  return response
}

export const updateReportTemplate = async (reportId: string, payload: TReportTemplateDetails) => {
  const reportType = payload.reportType || 'SCEN'
  const response = await axiosInstance.put(`/report/templates?reportType=${reportType}&reportId=${reportId}`, payload)
  return response
}

export const getReportTemplateDetails = async (reportType: string, reportId: string) => {
  const response = await axiosInstance.get(`/report/templates?reportType=${reportType}&reportId=${reportId}`)
  return response
}

export const validateCriteriaForTab = async (filterTo: string, criteriaData: any[]) => {
  try {
    const response = await axiosInstance.post(
      `/screening-templates/computeAddSubTitle?filterTo=${filterTo}`,
      criteriaData,
    )
    return response.data
  } catch (error: any) {
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      headers: error.response?.headers,
    })
    throw error
  }
}

export const getPortfolioDates = async (portfolioName: string) => {
  const response = await axiosInstance.get(`portfolio/rundates?portfolioName=${portfolioName}`)
  return response.data
}

export const deleteTemplate = async (reportType: string, reportId: string) => {
  const response = await axiosInstance.delete(`/report/templates?reportType=${reportType}&reportId=${reportId}`)
  return response.data[0]
}

export const runReportTemplate = async (payload: IRunReportTemplateDTO) => {
  const response = await axiosInstance.post('/adhoc/runScenarioTemplate', payload)
  return response.data as IRunReportTemplateDTO
}
export const runReportTemplate2 = async (payload: IScheduleRunReportDTO2) => {
  const response = await axiosInstance.post('/reportTemplateRun/runnow', payload)
  return response.data as IScheduleReportSimpleReturn2
}

export const scheduleReportTemplate = async (payload: IScheduleRunReportDTO) => {
  const response = await axiosInstance.post('/adhoc/runScenarioTemplate/schedule', payload)
  return response.data as IScheduleRunReportDTO
}

export const scheduleReportTemplate2 = async (payload: IScheduleRunReportDTO2) => {
  const response = await axiosInstance.post('/reportTemplateRun/schedule', payload)
  return response.data as IScheduleReportSimpleReturn2
}

export const updateScheduleReportTemplate2 = async (payload: IScheduleRunReportDTO2) => {
  const response = await axiosInstance.put(`/reportTemplateRun/schedule/${payload.id}`, payload)
  return response.data as IScheduleReportSimpleReturn2
}

// GFPAH Template Service Functions
export const createGFPAReportTemplate = async (payload: TGFPAReportTemplatePayload) => {
  const response = await axiosInstance.post('/report/templates/GFPAH', payload)
  return response
}

export const updateGFPAReportTemplate = async (reportId: string, payload: TGFPAReportTemplatePayload) => {
  const response = await axiosInstance.put(`/report/templates/GFPAH?reportId=${reportId}`, payload)
  return response
}

export const getGFPAReportTemplateDetails = async (reportId: string) => {
  const response = await axiosInstance.get(`/report/templates?reportType=GFPAH&reportId=${reportId}`)
  return response
}
